import { useEffect, useRef, useState } from "react";
import { Bot, X, Send, History, Plus, BookOpen } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useAssist } from "./useAssist";
import { AssistMessages } from "./AssistMessages";
import { AssistHistory } from "./AssistHistory";
import { HumanButton } from "./HumanButton";

const Head = ({ t, onHistory, onNew, onGuide, onClose }) => (
  <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-gradient-to-r from-[#a746ff]/15 to-transparent">
    <div className="w-8 h-8 rounded-lg bg-[#a746ff]/25 grid place-items-center"><Bot className="w-4 h-4 text-[#c79bff]" /></div>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-display font-semibold text-white" data-testid="assist-title">{t("as.title")}</div>
      <div className="text-[10.5px] text-[#22e3a1] font-mono flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#22e3a1] animate-pulse" />{t("as.online")}</div>
    </div>
    <button onClick={onGuide} title={t("gd.nav")} data-testid="assist-guide-btn" className="p-1.5 text-slate-400 hover:text-white"><BookOpen className="w-4 h-4" /></button>
    <button onClick={onHistory} title={t("as.history")} data-testid="assist-history-btn" className="p-1.5 text-slate-400 hover:text-white"><History className="w-4 h-4" /></button>
    <button onClick={onNew} title={t("as.new")} data-testid="assist-new-btn" className="p-1.5 text-slate-400 hover:text-white"><Plus className="w-4 h-4" /></button>
    <button onClick={onClose} data-testid="assist-close-btn" className="p-1.5 text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
  </div>
);

export const AssistBubble = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [hist, setHist] = useState(false);
  const [text, setText] = useState("");
  const a = useAssist(t);
  const input = useRef(null);

  useEffect(() => {
    const h = (e) => { setOpen(true); setHist(false); if (e.detail?.text) setText(e.detail.text); setTimeout(() => input.current?.focus(), 80); };
    window.addEventListener("cx-assist-open", h);
    return () => window.removeEventListener("cx-assist-open", h);
  }, []);

  const submit = (e) => { e?.preventDefault(); const v = text; setText(""); a.send(v); };

  return (
    <>
      {open && (
        <div className="fixed z-50 right-3 left-3 sm:left-auto bottom-[156px] md:bottom-24 sm:w-[400px] h-[min(600px,calc(100vh-190px))] md:h-[min(600px,calc(100vh-130px))] rounded-2xl border border-white/10 bg-[#0a0d18]/95 backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,0.6)] flex flex-col overflow-hidden cx-page-in" data-testid="assist-panel">
          <Head t={t} onClose={() => setOpen(false)} onHistory={() => setHist(true)} onNew={() => a.pick(null)} onGuide={() => { nav("/guide"); setOpen(false); }} />
          <div className="relative flex-1 min-h-0">
            <div className="absolute inset-0 overflow-y-auto cx-scroll"><AssistMessages messages={a.messages} busy={a.busy} t={t} onSuggest={(q) => a.send(q)} /></div>
            {hist && <AssistHistory t={t} current={a.sessionId} onClose={() => setHist(false)} onPick={(id) => { a.pick(id); setHist(false); }} />}
          </div>
          <div className="border-t border-white/10 px-3 pt-2.5 pb-1">
            <form onSubmit={submit} className="flex items-end gap-2">
              <textarea ref={input} rows={1} value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} placeholder={t("as.ph")} data-testid="assist-input"
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) submit(e); }}
                className="flex-1 resize-none max-h-28 rounded-lg bg-[#070912] border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#2f7bff]" />
              <button type="submit" disabled={a.busy || !text.trim()} data-testid="assist-send-btn" className="cx-btn rounded-lg p-2.5 disabled:opacity-40"><Send className="w-4 h-4" /></button>
            </form>
            <HumanButton t={t} sessionId={a.sessionId} ticketId={a.ticketId} onDone={a.setTicketId} demo={user?.role === "demo"} />
          </div>
        </div>
      )}
      <button onClick={() => setOpen((o) => !o)} data-testid="assist-bubble-btn" aria-label={t("as.title")}
        className="fixed z-50 right-4 bottom-[88px] md:bottom-6 w-14 h-14 rounded-full grid place-items-center bg-gradient-to-br from-[#a746ff] to-[#2f7bff] shadow-[0_8px_30px_rgba(167,70,255,0.45)] hover:scale-105 active:scale-95 transition-transform">
        {open ? <X className="w-6 h-6 text-white" /> : <Bot className="w-6 h-6 text-white" />}
      </button>
    </>
  );
};
